<template>
  <pf-data-list>
    <pf-data-list-item v-for="row in projects" :key="row.nome" :expandable="!projectWithNoDetails(row)">
      <pf-data-list-cell>
        <pf-title h="4">{{ row.nome }}</pf-title>
        {{ $date(row.data) }}
      </pf-data-list-cell>

      <pf-data-list-action>
        <template v-if="isOpportunity(row) && row.stato === 'attesa'">
          <pf-button variant="danger" small @click="feedback(row.id, false)">
            <template #icon><xmark-icon /></template>
            <span class="pf-v6-u-display-none pf-v6-u-display-block-on-md">{{ $t('Rifiuta') }}</span>
          </pf-button>
          <pf-button variant="primary" small @click="feedback(row.id, true)">
            <template #icon><check-icon /></template>
            {{ $t('Accetta') }}
          </pf-button>
        </template>
        <template v-else>
          <pf-label
            :color="row.stato === 'accettata' ? 'green' : (row.stato === 'rifiutata' ? 'red' : 'grey')"
          >
            <template #icon>
              <component :is="row.stato === 'accettata' ? CircleCheckIcon : row.stato === 'rifiutata' ? XmarkIcon : CircleInfoIcon" />
            </template>{{ row.stato }}

            <form-help-tooltip v-if="row.stato === 'scaduta'" :text="$t('Un\'opportunità scade se è passato troppo tempo dalla richiesta o se è già stata accettata')" />
          </pf-label>
        </template>
      </pf-data-list-action>

      <pf-data-list-content>
        <pf-grid gutter>
          <pf-grid-item sm="6" sm-row-span="3">
            <pf-content>
              <p v-if="row.descrizione"><strong>{{ $t('Descrizione') }}</strong>: {{ row.descrizione }}</p>
              <p v-if="row.dominio"><strong>{{ $t('Dominio') }}</strong>: {{ row.dominio }}</p>
              <p v-if="row.tempistiche"><strong>{{ $t('Tempistiche') }}</strong>: {{ row.tempistiche }}</p>
              <p v-if="row.budget"><strong>{{ $t('Budget') }}</strong>: {{ row.budget }}</p>

              <template v-if="isOpportunity(row)">
                <template v-if="row.stato === 'accettata'">
                  <p v-if="row.cliente"><strong>{{ $t('Cliente') }}</strong>: {{ row.cliente }}</p>
                  <span v-if="row.contatti">{{ row.contatti }}</span>
                </template>
                <p v-else><i>{{ $t('I dati del richiedente vengono mostrati solo per le opportunità accettate.') }}</i></p>
              </template>
              <template v-else>
                <template v-if="row.stato === 'accettata'">
                  <p v-if="row.partner">
                    <strong>{{ $t('Partner assegnato') }}</strong>: {{ row.partner }}
                  </p>
                </template>
                <p v-else><i>{{ $t('I dati del partner verranno mostrati una volta che il progetto verrà accettato.') }}</i></p>
              </template>
            </pf-content>
          </pf-grid-item>

          <pf-grid-item lg="2" sm="6" v-if="row.skills.length">
            <pf-title h="4">{{ $t('Skill') }}:</pf-title>
            <pf-list>
              <pf-list-item v-for="s in row.skills" :key="s">{{ s }}</pf-list-item>
            </pf-list>
          </pf-grid-item>
          <pf-grid-item lg="2" sm="6" v-if="row.tech_skills.length">
            <pf-title h="4">{{ $t('Tecnologie') }}:</pf-title>
            <pf-list>
              <pf-list-item v-for="ts in row.tech_skills" :key="ts">{{ ts }}</pf-list-item>
            </pf-list>
          </pf-grid-item>
          <pf-grid-item lg="2" sm="6" v-if="row.business_sectors.length">
            <pf-title h="4">{{ $t('Settori merceologici') }}:</pf-title>
            <pf-list>
              <pf-list-item v-for="bs in row.business_sectors" :key="bs">{{ bs }}</pf-list-item>
            </pf-list>
          </pf-grid-item>
        </pf-grid>
      </pf-data-list-content>
    </pf-data-list-item>
  </pf-data-list>
</template>

<script lang="ts" setup generic="Variant extends 'project' | 'opportunity'">
import { $t as _t } from '@/i18n';
import { type Opportunity, type Project, OpportunityResource } from '../resources';
import FormHelpTooltip from '@/components/FormHelpTooltip.vue';
import CircleCheckIcon from '@vue-patternfly/icons/circle-check-icon';
import CircleInfoIcon from '@vue-patternfly/icons/circle-info-icon';
import XmarkIcon from '@vue-patternfly/icons/xmark-icon';
import CheckIcon from '@vue-patternfly/icons/check-icon';

const props = defineProps<{
  variant: Variant;
  projects: (Variant extends 'project' ? Project : Opportunity)[];
}>();

const emit = defineEmits<{
  (e: 'feedback'): void
}>();

function isOpportunity(project: Project| Opportunity): project is Opportunity {
  return props.variant === 'opportunity';
}

// E' possibile creare dalla intranet opportunità senza dettagli
function projectWithNoDetails(project: Project| Opportunity) {
  return !project.descrizione && !project.budget && !project.tempistiche && !project.skills.length && !project.tech_skills.length && !project.business_sectors.length;
}

async function feedback(id: number, accept: boolean) {
  const confirmMessage = accept ? _t("Confermi di voler accettare l'opportunità? Ti daremo accesso ai dati del cliente per poterlo contattare direttamente") : _t("Stai rifiutando l'opportunità, confermi di voler procedere?");

  if (!confirm(confirmMessage)) {
    return;
  }

  await new OpportunityResource().put(id, {
    stato: accept ? 'accettata' : 'rifiutata',
  });

  emit('feedback');
}
</script>
